import { styled } from '@porsche-code/kado-styled-system/jsx';
import { PButton } from '@porsche-design-system/components-react';
import { useEditorState } from 'platejs/react';
import { useState, type ComponentType } from 'react';

import { ColorPickerInput } from '../../../ColorPickerInput';
import { Popover } from '../../../Popover';
import { ToolbarButton } from '../../ToolbarButton';

export const PopoverContent = styled('div', {
  base: {
    display: 'grid',
    gap: 'sm',
    minWidth: '16rem',
  },
});

export const PopoverActionContainer = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 'sm',
  },
});

type EditorState = ReturnType<
  (typeof import('platejs/react'))['useEditorState']
>;

export const getCurrentMarkValue = (editor: EditorState, key: string) => {
  const marks = editor.api.marks?.() as
    | Record<string, string>
    | null
    | undefined;

  return marks?.[key] ?? '';
};

export const setLeafMark = (
  editor: EditorState,
  key: string,
  value?: string,
) => {
  if (!value) {
    editor.tf.removeMark(key);
    editor.tf.focus();
    return;
  }

  editor.tf.addMark(key, value);
  editor.tf.focus();
};

const ColorButtonContent = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
    height: '100%',
  },
});

export const ColorPopoverControl = ({
  clearLabel,
  icon: Icon,
  label,
  markKey,
}: {
  clearLabel: string;
  icon: ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  markKey: string;
}) => {
  const editor = useEditorState();
  const [isOpen, setIsOpen] = useState(false);
  const value = getCurrentMarkValue(editor, markKey);

  return (
    <Popover
      closeOnFocusOut={false}
      open={isOpen}
      onChange={setIsOpen}
      target={
        <ToolbarButton active={!!value || isOpen} tooltip={label} type="button">
          <ColorButtonContent>
            <Icon style={{ color: value || 'currentColor' }} />
          </ColorButtonContent>
        </ToolbarButton>
      }
    >
      <PopoverContent>
        <ColorPickerInput
          label={label}
          value={value || '#000000'}
          onChange={(event: { target: { value?: string } }) => {
            setLeafMark(editor, markKey, event.target.value);
          }}
        />

        <PopoverActionContainer>
          <PButton
            icon="close"
            type="button"
            variant="secondary"
            onClick={() => {
              setLeafMark(editor, markKey, undefined);
              setIsOpen(false);
            }}
          >
            {clearLabel}
          </PButton>
        </PopoverActionContainer>
      </PopoverContent>
    </Popover>
  );
};
